import { cx } from '@emotion/css';
import React, { ReactElement, useState } from 'react';
import { uuidv4 } from '../../../../../state/src/utils/uuid';
import { SizeVariants } from '../../../theme/themes';
import { withCubicleFallback } from '../../../utils/CubicleFallback';
import { makeCSS } from '../../../utils/makeCSS';
import { InputFieldTempate } from './InputFieldTempate';
import { Message } from './Message';
import { ContextMenu } from '../../molecules/ContextMenu';
import { Placement } from '@floating-ui/dom';

/**
 * This component allows users to enter a password, with an option to show what they’ve entered as plain text.
 * This allows users to visually check their password before they submit it, which helps them reduce errors and choose passwords that are more unique and secure.
 *
 * The Password component uses the Message component to display a message for invalid password or other extra information.
 */
export interface PasswordProps {
  /** Placeholder of the search input */
  placeholder?: string;
  /***/
  onEnter?(value: string): void;
  /** Different sizes*/
  size?: SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  /** Disable the search input*/
  disabled?: boolean;
  /** Text for the message */
  messageLabel?: string;
  /** Display or hide message*/
  showMessage?: boolean;
  /** don't ubfuscate the password initially */
  startVisible?: boolean;
  /** Controlled input value */
  value?: string;
  /** change handler callback */
  onChange?(value: string): void;
  /** set to false to try to prevent browser autofill */
  autoFill?: boolean;
  tooltips?: ReactElement;
  /**define the type of autocomplete we want to use*/
  autoComplete?: string;
  autoFocus?: boolean;
  passwordRef?: React.LegacyRef<HTMLInputElement>;
  //placement of the tooltip
  tooltipPlacement?: Placement;
}

const useCSS = makeCSS(({ theme }) => {
  return {
    passwordField: {
      width: '100%',
      alignContent: 'bottom',
      resize: 'none',
      color: theme.color.text['01'],
      fontFamily: 'inherit',
      fontWeight: 'inherit',
      fontSize: 'inherit',
      background: 'inherit',
      marginTop: 0,
      outline: 'none',
      border: 'none',
      paddingTop: theme.spacing[3],
      paddingBottom: theme.spacing[3],
      '&::placeholder': {
        color: theme.color.text['04']
      }
    },
    input: {
      'input:is(:-webkit-autofill, :autofill)': {
        WebkitTextFillColor: theme.color.text['01'],
        transition: 'background-color 5000s ease-in-out 0s'
      }
    }
  };
});

const Password = (props: PasswordProps) => {
  const {
    placeholder = 'Password...',
    size = SizeVariants.md,
    disabled,
    onEnter,
    messageLabel = 'Invalid password',
    showMessage,
    onChange,
    startVisible,
    value,
    autoFill = false,
    tooltips,
    autoComplete,
    autoFocus,
    passwordRef,
    tooltipPlacement = 'right',
    ...restProps
  } = props;

  const [visible, setVisible] = useState(startVisible);
  const [passwordInput, setPasswordInput] = useState(props.value || '');

  const classes = useCSS();

  const handleVisibility = (event) => {
    setVisible(!visible);
  };
  const handleChange = (event) => {
    setPasswordInput(event.target.value);
    onChange?.(event.target.value);
  };

  const handleEnter = (event) => {
    if (event.keyCode === 13) {
      event.preventDefault();
      onEnter?.(passwordInput);
    }
  };

  React.useLayoutEffect(() => {
    if (passwordInput !== value) {
      setPasswordInput(value);
    }
  }, [value]);

  //set the type of the input before the render to make sure the value is determined efficiently
  const inputType = visible ? 'text' : 'password';

  const [inputRef, setInputRef] = useState(null);

  return (
    <>
      <div ref={setInputRef}>
        <InputFieldTempate
          width={80}
          label={''}
          size={size}
          iconLeft={'lock'}
          iconRight={visible ? 'visibility' : 'visibility_off'}
          disabled={disabled}
          onRightIconClick={handleVisibility}
        >
          <span className={`${classes.input}`}>
            <input
              {...restProps}
              ref={passwordRef}
              disabled={disabled}
              className={cx(classes.passwordField)}
              type={inputType}
              placeholder={placeholder}
              onKeyDown={handleEnter}
              onChange={handleChange}
              value={passwordInput}
              id={!autoFill ? uuidv4() : 'password'}
              name={!autoFill ? uuidv4() : 'password'}
              autoCorrect={!autoFill ? 'off' : ''}
              spellCheck={!autoFill}
              autoComplete={!autoFill ? uuidv4() : autoComplete}
              autoFocus={autoFocus}
            />
          </span>
        </InputFieldTempate>
      </div>
      {inputRef && tooltips && (
        <ContextMenu
          anchor={inputRef}
          placement={tooltipPlacement}
          width={'auto'}
        >
          {tooltips}
        </ContextMenu>
      )}

      {showMessage && (
        <Message
          label={messageLabel}
          messageColor={'critical'}
          // icon={''}
        />
      )}
    </>
  );
};

const Default = withCubicleFallback(Password);
export default Default;
export { Password };
